import { useGetSessionToken } from '@homestake/shared/hooks';
import { batch } from '@legendapp/state';
import { useMutation } from '@tanstack/react-query';
import Decimal from 'decimal.js';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { postApiGameStartMutation } from '../api/__generated__/@tanstack/react-query.gen';
import {
  configurationState$,
  gameState$,
  manualBetState$,
  ratAnimationState$,
  towersState$,
  winDisplayState$,
} from '../state';
import { freeBetState$ } from '../state/free-bet.state';
import { sessionState$ } from '../state/session.state';
import { walletState$ } from '../state/wallet.state';
import { showErrorNotification } from '../utils';
import { useBackendErrorNotification } from './useBackendErrorNotification.hook';
import { useClickSound } from './useClickSound.hook';

export const useStartManualBet = () => {
  const { t } = useTranslation();
  const { token } = useGetSessionToken();
  const { showBackendErrorNotification } = useBackendErrorNotification();

  const { playSound: playClickSound } = useClickSound();

  const mutation = useMutation({
    ...postApiGameStartMutation(),
    onSuccess: ({ gameRoundId, balance, baseBonusResponseDto }) => {
      batch(() => {
        if (gameRoundId) {
          freeBetState$.update(baseBonusResponseDto, false);

          winDisplayState$.hideWinDisplay();
          sessionState$.gameRoundId.set(gameRoundId);
          manualBetState$.setIsBetting(true);
          walletState$.setBalance(balance ?? 0);
          towersState$.resetBricks();
          ratAnimationState$.setAnimationType('idle');
          playClickSound();
        }
      });
    },
    onError: (error) => {
      showBackendErrorNotification(error);
    },
  });

  const handleStartManualBet = useCallback(() => {
    if (!token) {
      showErrorNotification({
        title: t('notifications.errorTitle'),
        message: t('notifications.noTokenFoundMessage'),
        dataTestId: 'start-bet-no-token',
      });
      return;
    }

    // Check if user has sufficient balance
    const betAmount = manualBetState$.betAmount.peek();
    const currentBalance = walletState$.balance.peek();

    if (currentBalance < betAmount) {
      showErrorNotification({
        title: t('notifications.insufficientBalanceTitle'),
        message: t('notifications.insufficientBalanceMessage'),
        dataTestId: 'insufficient-balance-start',
      });
      return;
    }

    mutation.mutate({
      body: {
        amount: new Decimal(betAmount)
          .mul(configurationState$.coeficient.peek())
          .toNumber(),
        token,
        pattern: gameState$.pattern.peek(),
        gameRoundId: sessionState$.gameRoundId.peek(),
      },
    });
  }, [mutation, token, t]);

  return { mutation, handleStartManualBet };
};
